import React from 'react';
import { Modal, Form, Input, Button, Space, Select, message, Upload, Typography, Grid, Alert, theme } from 'antd';
import { InboxOutlined, DeleteOutlined } from '@ant-design/icons';

const { Option } = Select;
const { Dragger } = Upload;
const { Text } = Typography;
const { useBreakpoint } = Grid;



const CompanyModal = ({ 
  visible, 
  onCancel, 
  onFinish, 
  form, 
  selectedCompany, 
  businessActivities,
  loading 
}) => {
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  const [logoBase64, setLogoBase64] = React.useState(null);
  const [logoChanged, setLogoChanged] = React.useState(false);

  // Efecto para inicializar el logo cuando se selecciona una empresa
  React.useEffect(() => {
    if (visible && selectedCompany) {
      setLogoBase64(selectedCompany.logo || null);
      setLogoChanged(false);
    } else if (!visible) {
      setLogoBase64(null);
      setLogoChanged(false);
    }
  }, [visible, selectedCompany]);

  const handleBeforeUpload = (file) => {
    // Validar tamaño (50KB)
    if (file.size > 50 * 1024) {
      message.error('El logo no puede exceder los 50KB');
      return Upload.LIST_IGNORE;
    }

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      message.error('El archivo debe ser una imagen');
      return Upload.LIST_IGNORE;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result;
      
      // Validar dimensiones
      const img = new Image();
      img.src = base64;
      img.onload = () => {
        if (img.width > 300 || img.height > 300) {
          message.error(`El logo no puede exceder las dimensiones de 300x300px (Actual: ${img.width}x${img.height}px)`);
          return;
        }
        
        setLogoBase64(base64);
        setLogoChanged(true);
      };
    };
    reader.onerror = () => {
      message.error('Error al leer el archivo');
    };
    reader.readAsDataURL(file);

    return false; // Prevent auto upload
  };

  const handleRemoveLogo = () => {
    setLogoBase64(null);
    setLogoChanged(true);
  };

  const handleSubmit = (values) => {
    const payload = { ...values };
    // Solo enviamos el logo si ha sido modificado (nuevo o eliminado)
    if (logoChanged) {
      payload.logo = logoBase64;
    }
    onFinish(payload);
  };
  return (
    <Modal
      title={selectedCompany ? 'Editar Empresa' : 'Nueva Empresa'}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={screens.xs ? '100%' : 600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        {!selectedCompany && (
          <Alert
            message="Al crear una empresa, se generan impuestos por default"
            type="info"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}
        <Form.Item
          name="tax_name"
          label="Razón Social"
          rules={[{ required: true, message: 'La razón social es requerida' }]}
        >
          <Input placeholder="Ingresa la razón social" />
        </Form.Item>

        <Form.Item
          name="tax_id"
          label="RFC"
          rules={[
            { required: true, message: 'El RFC es requerido' },
            { pattern: /^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/, message: 'RFC inválido' }
          ]}
        >
          <Input placeholder="RFC de la empresa" style={{ textTransform: 'uppercase' }} />
        </Form.Item>

        <Form.Item
          name="fiscal_regimen"
          label="Régimen Fiscal"
          rules={[{ required: true, message: 'El régimen fiscal es requerido' }]}
        >
          <Select placeholder="Selecciona el régimen fiscal">
            <Option value="601">601 - General de Ley Personas Morales</Option>
            <Option value="603">603 - Personas Morales con Fines no Lucrativos</Option>
            <Option value="605">605 - Sueldos y Salarios e Ingresos Asimilados a Salarios</Option>
            <Option value="606">606 - Arrendamiento</Option>
            <Option value="607">607 - Régimen de Enajenación o Adquisición de Bienes</Option>
            <Option value="608">608 - Demás ingresos</Option>
            <Option value="610">610 - Residentes en el Extranjero sin Establecimiento Permanente en México</Option>
            <Option value="611">611 - Ingresos por Dividendos (socios y accionistas)</Option>
            <Option value="612">612 - Personas Físicas con Actividades Empresariales y Profesionales</Option>
            <Option value="614">614 - Ingresos por intereses</Option>
            <Option value="615">615 - Régimen de los ingresos por obtención de premios</Option>
            <Option value="616">616 - Sin obligaciones fiscales</Option>
            <Option value="620">620 - Sociedades Cooperativas de Producción que optan por diferir sus ingresos</Option>
            <Option value="621">621 - Incorporación Fiscal</Option>
            <Option value="622">622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras</Option>
            <Option value="623">623 - Opcional para Grupos de Sociedades</Option>
            <Option value="624">624 - Coordinados</Option>
            <Option value="625">625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas</Option>
            <Option value="626">626 - Régimen Simplificado de Confianza</Option>
            <Option value="628">628 - Hidrocarburos</Option>
            <Option value="629">629 - De los Regímenes Fiscales Preferentes y de las Empresas Multinacionales</Option>
            <Option value="630">630 - Enajenación de acciones en bolsa de valores</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="business_activity_codes"
          label="Actividades empresariales"
        >
          <Select mode="multiple" placeholder="Selecciona una o más actividades">
            {(businessActivities || []).map((a) => (
              <Option key={a.code} value={a.code}>
                {a.name} ({a.code})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="use_tax"
          label="Uso CFDI"
          rules={[{ required: true, message: 'El uso CFDI es requerido' }]}
        >
          <Select placeholder="Selecciona el uso CFDI">
            <Option value="G01">G01 - Adquisición de mercancías</Option>
            <Option value="G02">G02 - Devoluciones, descuentos o bonificaciones</Option>
            <Option value="G03">G03 - Gastos en general</Option>
            <Option value="I01">I01 - Construcciones</Option>
            <Option value="I02">I02 - Mobilario y equipo de oficina por inversiones</Option>
            <Option value="I03">I03 - Equipo de transporte</Option>
            <Option value="I04">I04 - Equipo de computo y accesorios</Option>
            <Option value="I05">I05 - Dados, troqueles, moldes, matrices y herramental</Option>
            <Option value="I06">I06 - Comunicaciones telefónicas</Option>
            <Option value="I07">I07 - Comunicaciones satelitales</Option>
            <Option value="I08">I08 - Otra maquinaria y equipo</Option>
            <Option value="D01">D01 - Honorarios médicos, dentales y gastos hospitalarios</Option>
            <Option value="D02">D02 - Gastos médicos por incapacidad o discapacidad</Option>
            <Option value="D03">D03 - Gastos funerales</Option>
            <Option value="D04">D04 - Donativos</Option>
            <Option value="D05">D05 - Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)</Option>
            <Option value="D06">D06 - Aportaciones voluntarias al SAR</Option>
            <Option value="D07">D07 - Primas por seguros de gastos médicos</Option>
            <Option value="D08">D08 - Gastos de transportación escolar obligatoria</Option>
            <Option value="D09">D09 - Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones</Option>
            <Option value="D10">D10 - Pagos por servicios educativos (colegiaturas)</Option>
            <Option value="P01">P01 - Por definir</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="tax_location"
          label="Código Postal Fiscal"
          rules={[
            { required: true, message: 'El código postal es requerido' },
            { pattern: /^[0-9]{5}$/, message: 'El código postal debe tener 5 dígitos' }
          ]}
        >
          <Input placeholder="Código postal de 5 dígitos" maxLength={5} />
        </Form.Item>

        <Form.Item label="Logo (opcional)">
          {!logoBase64 ? (
            <Dragger
              accept="image/*"
              maxCount={1}
              showUploadList={false}
              beforeUpload={handleBeforeUpload}
              style={{ padding: '20px 0' }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Haz clic o arrastra tu logo aquí</p>
              <p className="ant-upload-hint">
                Soporta imágenes JPG, PNG (máx. 50KB y 300x300px)
              </p>
            </Dragger>
          ) : (
            <div style={{ 
              marginTop: '8px', 
              padding: '24px', 
              border: `1px dashed ${token.colorBorder}`, 
              borderRadius: '8px', 
              textAlign: 'center',
              background: token.colorFillQuaternary,
              position: 'relative'
            }}>
              <div style={{ marginBottom: '16px', height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img 
                  src={logoBase64} 
                  alt="Logo de la empresa" 
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
                />
              </div>
              <Space direction="vertical" size="small">
                <Text type="secondary" style={{ fontSize: '12px' }}>Vista previa del logo</Text>
                <Button 
                  danger 
                  type="default"
                  icon={<DeleteOutlined />} 
                  onClick={handleRemoveLogo}
                >
                  Quitar logo y subir otro
                </Button>
              </Space>
            </div>
          )}
        </Form.Item>

        <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
          <Space>
            <Button onClick={onCancel}>
              Cancelar
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {selectedCompany ? 'Actualizar' : 'Crear'} Empresa
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CompanyModal;
